import Plotly from 'plotly.js-basic-dist-min';
import { toPng } from 'html-to-image';

type PlotlyDiv = HTMLElement & { _fullData: never };

export interface CompositorInput {
  plotDiv: HTMLElement;
  /** ClassificationPanel root element to composite below the chart, or null to skip. */
  classificationPanelEl: HTMLElement | null;
  format: 'png' | 'svg';
  width?: number;
  height?: number;
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function capturePanelPng(panelEl: HTMLElement): Promise<string | null> {
  try {
    return await toPng(panelEl, { backgroundColor: '#ffffff' });
  } catch {
    return null;
  }
}

async function compositePng(
  chartDataUrl: string,
  panelEl: HTMLElement | null,
  chartWidth: number,
  chartHeight: number,
): Promise<Blob> {
  const chartImg = await loadImage(chartDataUrl);

  if (panelEl) {
    const panelDataUrl = await capturePanelPng(panelEl);
    if (panelDataUrl) {
      const panelImg = await loadImage(panelDataUrl);
      const canvas = document.createElement('canvas');
      canvas.width = chartWidth;
      canvas.height = chartHeight + panelImg.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(chartImg, 0, 0, chartWidth, chartHeight);
      ctx.drawImage(panelImg, 0, chartHeight, chartWidth, panelImg.naturalHeight);
      return new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), 'image/png'),
      );
    }
  }

  // Chart-only path (no panel, or panel capture failed)
  const canvas = document.createElement('canvas');
  canvas.width = chartWidth;
  canvas.height = chartHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(chartImg, 0, 0);
  return new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b!), 'image/png'),
  );
}

async function compositeSvg(
  svgDataUrl: string,
  panelEl: HTMLElement | null,
): Promise<Blob> {
  // Plotly SVG export is URL-encoded: data:image/svg+xml,...
  const svgText = svgDataUrl.startsWith('data:image/svg+xml;base64,')
    ? atob(svgDataUrl.slice('data:image/svg+xml;base64,'.length))
    : decodeURIComponent(svgDataUrl.slice('data:image/svg+xml,'.length));

  if (panelEl) {
    const panelDataUrl = await capturePanelPng(panelEl);
    if (panelDataUrl) {
      const panelImg = await loadImage(panelDataUrl);
      const panelH = panelImg.naturalHeight;
      const panelW = panelImg.naturalWidth;

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgEl = doc.documentElement;

      const currentH = parseFloat(svgEl.getAttribute('height') ?? '700');
      svgEl.setAttribute('height', String(currentH + panelH));

      const vb = svgEl.getAttribute('viewBox');
      if (vb) {
        const parts = vb.trim().split(/[\s,]+/);
        if (parts.length === 4) {
          svgEl.setAttribute(
            'viewBox',
            `${parts[0]} ${parts[1]} ${parts[2]} ${parseFloat(parts[3]) + panelH}`,
          );
        }
      }

      const imageEl = doc.createElementNS('http://www.w3.org/2000/svg', 'image');
      imageEl.setAttribute('x', '0');
      imageEl.setAttribute('y', String(currentH));
      imageEl.setAttribute('width', String(panelW));
      imageEl.setAttribute('height', String(panelH));
      imageEl.setAttribute('href', panelDataUrl);
      svgEl.appendChild(imageEl);

      return new Blob([new XMLSerializer().serializeToString(doc)], {
        type: 'image/svg+xml',
      });
    }
  }

  return new Blob([svgText], { type: 'image/svg+xml' });
}

/**
 * Composites a Plotly chart image with an optional ClassificationPanel snapshot,
 * returning a Blob ready for download or zip bundling.
 */
export async function compositeToBlob(input: CompositorInput): Promise<Blob> {
  const { plotDiv, classificationPanelEl, format, width = 1600, height = 700 } = input;

  const dataUrl = await Plotly.toImage(plotDiv as PlotlyDiv, {
    format: format === 'svg' ? 'svg' : 'png',
    width,
    height,
  });

  return format === 'png'
    ? compositePng(dataUrl, classificationPanelEl, width, height)
    : compositeSvg(dataUrl, classificationPanelEl);
}

/** Composites and triggers a browser download of the result. */
export async function compositeAndDownload(
  input: CompositorInput,
  filename: string,
): Promise<void> {
  const blob = await compositeToBlob(input);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
